import type { ColumnsType } from "antd/es/table";

import { AppStrings } from "../../../utils/constants/AppStrings";
import { formatDecimal } from "../../ReportingSummary/reportingSummaryUtils";
import { formatCurrency } from "../../../utils/financialFormatters";

const pct = (n: number | undefined) => `${formatDecimal(n ?? 0, 2)}%`;

/**
 * API / UI column keys; must match `SORT_FIELD_TO_API` in `casinoGamesReportApi.js`.
 */
export const CASINO_GAMES_SORT_FIELD_MAP = {
  name: "name",
  providerName: "subprovider",
  category: "category",
  uniquePlayers: "unique_players",
  rounds: "rounds",
  avgRounds: "avg_rounds",
  turnover: "turnover",
  wins: "wins",
  rtpPct: "rtp",
  ggr: "ggr",
  avgBet: "avg_bet",
} as const;

export type CasinoGamesSortKey = keyof typeof CASINO_GAMES_SORT_FIELD_MAP;

export type CasinoGamesRow = {
  key: string;
  name: string;
  providerName: string;
  providerLogoUrl: string | null;
  category: string;
  uniquePlayers: number;
  rounds: number;
  avgRounds: number;
  turnover: number;
  wins: number;
  rtpPct: number;
  ggr: number;
  avgBet: number;
};

export type CasinoGamesTableSortState = {
  field: CasinoGamesSortKey | null;
  order: "ascend" | "descend" | null;
};

const defaultSortState: CasinoGamesTableSortState = {
  field: null,
  order: null,
};

/**
 * Server-side sort: `buildCasinoGamesSortParam` in the page.
 */
export function getCasinoGamesColumns(
  sortState: CasinoGamesTableSortState = defaultSortState,
  currencySymbol: string = "$",
): ColumnsType<CasinoGamesRow> {
  const { field: sortField, order: sortOrder } = sortState;
  const colSortOrder = (key: CasinoGamesSortKey) =>
    sortField === key ? sortOrder ?? undefined : undefined;
  const money = (v: number | undefined) => formatCurrency(v ?? 0, currencySymbol, 2);

  return [
    {
      title: AppStrings.NAME,
      dataIndex: "name",
      key: "name",
      sorter: true,
      sortOrder: colSortOrder("name"),
      width: 200,
      render: (value: string | undefined) => (
        <span className="casino-games-name-cell">{value ?? "—"}</span>
      ),
    },
    {
      title: AppStrings.CASINO_GAMES_COL_PROVIDER,
      dataIndex: "providerName",
      key: "providerName",
      sorter: true,
      sortOrder: colSortOrder("providerName"),
      align: "center",
      width: 160,
      render: (_: unknown, record: CasinoGamesRow) => (
        <div
          className="casino-games-provider-cell"
          title={record.providerName || undefined}
        >
          {record.providerLogoUrl ? (
            <img
              src={record.providerLogoUrl}
              alt=""
              className="casino-games-provider-logo"
            />
          ) : (
            <span className="casino-games-provider-name-fallback">
              {record.providerName || "—"}
            </span>
          )}
        </div>
      ),
    },
    {
      title: AppStrings.CASINO_GAMES_COL_CATEGORY,
      dataIndex: "category",
      key: "category",
      sorter: true,
      sortOrder: colSortOrder("category"),
      width: 160,
    },
    {
      title: AppStrings.CASINO_PROVIDERS_COL_UNIQUE_PLAYERS,
      dataIndex: "uniquePlayers",
      key: "uniquePlayers",
      sorter: true,
      sortOrder: colSortOrder("uniquePlayers"),
      align: "center",
      width: 192,
      render: (v: number | undefined) =>
        Number(v ?? 0).toLocaleString("en-US", { maximumFractionDigits: 0 }),
    },
    {
      title: AppStrings.CASINO_FINANCE_COL_ROUNDS,
      dataIndex: "rounds",
      key: "rounds",
      sorter: true,
      sortOrder: colSortOrder("rounds"),
      align: "center",
      width: 108,
      render: (v: number | undefined) =>
        Number(v ?? 0).toLocaleString("en-US", { maximumFractionDigits: 0 }),
    },
    {
      title: AppStrings.CASINO_PROVIDERS_COL_AVG_ROUNDS,
      dataIndex: "avgRounds",
      key: "avgRounds",
      sorter: true,
      sortOrder: colSortOrder("avgRounds"),
      align: "center",
      width: 128,
      render: (v: number | undefined) => formatDecimal(v ?? 0, 2),
    },
    {
      title: AppStrings.CASINO_FINANCE_COL_TURNOVER,
      dataIndex: "turnover",
      key: "turnover",
      sorter: true,
      sortOrder: colSortOrder("turnover"),
      align: "center",
      width: 120,
      render: (v: number | undefined) => money(v),
    },
    {
      title: AppStrings.CASINO_FINANCE_COL_WINS,
      dataIndex: "wins",
      key: "wins",
      sorter: true,
      sortOrder: colSortOrder("wins"),
      align: "center",
      width: 108,
      render: (v: number | undefined) => money(v),
    },
    {
      title: AppStrings.CASINO_FINANCE_COL_RTP,
      dataIndex: "rtpPct",
      key: "rtpPct",
      sorter: true,
      sortOrder: colSortOrder("rtpPct"),
      align: "center",
      width: 96,
      render: (v: number | undefined) => pct(v),
    },
    {
      title: AppStrings.CASINO_FINANCE_COL_GGR,
      dataIndex: "ggr",
      key: "ggr",
      sorter: true,
      sortOrder: colSortOrder("ggr"),
      align: "center",
      width: 108,
      render: (v: number | undefined) => {
        const amount = v ?? 0;
        const colorClass =
          amount > 0
            ? "casino-games-ggr-positive"
            : amount < 0
              ? "casino-games-ggr-negative"
              : undefined;
        return <span className={colorClass}>{money(amount)}</span>;
      },
    },
    {
      title: AppStrings.CASINO_FINANCE_COL_AVG_BET,
      dataIndex: "avgBet",
      key: "avgBet",
      sorter: true,
      sortOrder: colSortOrder("avgBet"),
      align: "center",
      width: 172,
      render: (v: number | undefined) => money(v),
    },
  ];
}
